import { FC, useMemo } from "react";
import {
  Alert,
  Button,
  Col,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Typography,
} from "antd";
import type { FormInstance } from "antd";
import type { CheckPriceCarrierItem } from "services/apiSpeedShipping";
import TransportLogo from "components/ManageProduct/CreateProduct/ProductSKUCreate/TransportLogo";
import { formatWithCommas } from "components/ManageProduct/CreateProduct/ProductSKUCreate/helpers";
import {
  getCheckPriceCarrierCode,
  normalizeSpeedLogo,
} from "../../ParcelShared/speedCarrierUtils";
import ParcelCarrierPanel from "../shipping/ParcelCarrierPanel";
import ParcelDigitsInput from "../shipping/ParcelDigitsInput";
import {
  ParcelFormSectionTitle,
  ParcelFormZone,
  parcelHighlightActionButtonStyle,
  parcelInputStyle,
  ParcelHintBox,
} from "../../manageParcelLayout";
import { useParcelPackagingSelection } from "../hooks/useParcelPackagingSelection";
import {
  canSearchParcelCarriers,
  shouldHideAutoDimensions,
  shouldShowDimensionInputFields,
  shouldShowManualDimensionFields,
} from "../parcelPackagingLogic";
import { normalizeCheckPriceCarrierList } from "services/apiSpeedShipping";

type ParcelProductShippingStepProps = {
  form: FormInstance;
  carriers: CheckPriceCarrierItem[];
  selectedCarrier: string;
  onSelectCarrier: (code: string) => void;
  speedHasSearched: boolean;
  checkingPrice?: boolean;
  onSearchCarriers: () => void;
  onPackagingChange?: () => void;
  sectionEnabled?: boolean;
  /** เครดิตดิบที่มีอยู่จริงของโรงงาน — ใช้เทียบกับค่าขนส่งที่เลือก ว่าเพียงพอไหม (ไม่คูณ multiplier) */
  availableCredit?: number;
  /** multiplier ปัจจุบัน — ใช้แค่แสดง "เครดิตที่ใช้" ให้ตรงกับโควต้าจริง */
  multiplier?: number;
};

const ParcelProductShippingStep: FC<ParcelProductShippingStepProps> = ({
  form,
  carriers,
  selectedCarrier,
  onSelectCarrier,
  speedHasSearched,
  checkingPrice,
  onSearchCarriers,
  onPackagingChange,
  sectionEnabled = true,
  availableCredit,
  multiplier,
}) => {
  const carrierList = normalizeCheckPriceCarrierList(carriers);
  const parcelName = String(Form.useWatch("parcel_name", form) ?? "").trim();
  const {
    parentList,
    packagingTypeLoading,
    childPackagingTypeLoading,
    showChildDropdown,
    effectiveParentId,
    childPackagingTypeData,
    selectedPackaging,
    packagingIncomplete,
    hideAutoDimensions,
    showDimensionInputs,
    boxWeight,
    boxWidth,
    boxLength,
    boxHeight,
    parentPackagingTypeId,
    packagingTypeId,
  } = useParcelPackagingSelection(form);

  const showManualDimensions = shouldShowManualDimensionFields(
    packagingIncomplete,
    showChildDropdown,
    selectedPackaging
  );
  const hideAutoDims = shouldHideAutoDimensions(showChildDropdown, selectedPackaging);
  const showDimInputs = shouldShowDimensionInputFields(
    packagingIncomplete,
    showManualDimensions,
    hideAutoDims
  );

  const dimSumInvalid =
    Number(boxWidth ?? 0) + Number(boxLength ?? 0) + Number(boxHeight ?? 0) > 1000;

  const canSearch = canSearchParcelCarriers({
    packagingIncomplete,
    boxWeight,
    boxWidth,
    boxLength,
    boxHeight,
    totalProductWeightGrams: 0,
  });

  const selectedItem = useMemo(
    () =>
      carrierList.find((c) => getCheckPriceCarrierCode(c) === selectedCarrier),
    [carrierList, selectedCarrier]
  );
  const parcelHintText = (() => {
    if (!sectionEnabled) return "กรอกที่อยู่ผู้รับในส่วนที่ 1 ให้ครบก่อน";
    if (!parcelName) return "กรอกชื่อพัสดุที่ต้องการส่ง";
    if (!parentPackagingTypeId) return "กรุณาเลือกประเภทบรรจุภัณฑ์";
    if (showChildDropdown && !packagingTypeId) return "กรุณาเลือกบรรจุภัณฑ์ย่อย";
    if (dimSumInvalid) return "กว้างยาวสูงรวมกันต้องไม่เกิน 1,000 ซ.ม.";
    if (!canSearch) return "กรุณากรอกขนาดกล่องและน้ำหนักให้ครบก่อนค้นหาขนส่ง";
    if (checkingPrice) return "กำลังค้นหาบริษัทขนส่ง...";
    if (speedHasSearched && carrierList.length === 0)
      return "ไม่พบบริษัทขนส่งสำหรับข้อมูลนี้ กรุณาปรับขนาดหรือน้ำหนักแล้วค้นหาใหม่";
    if (speedHasSearched && carrierList.length > 0) return "เลือกบริษัทขนส่งจากรายการ";
    return "ข้อมูลครบแล้ว กดปุ่มค้นหาบริษัทขนส่ง";
  })();

  const shippingPriceDisplay = selectedItem
    ? formatWithCommas(Math.ceil(Number(selectedItem.total ?? selectedItem.price ?? 0)))
    : "-";

  const handleParentChange = (value: string | undefined) => {
    const parent = parentList.find((p) => p.id === value);
    if (parent && !parent.has_child) {
      form.setFieldValue("packaging_type_id", value);
    } else {
      form.setFieldValue("packaging_type_id", undefined);
    }
    form.setFieldsValue({
      box_width: undefined,
      box_length: undefined,
      box_height: undefined,
    });
    onPackagingChange?.();
  };

  return (
    <ParcelFormZone>
      <ParcelFormSectionTitle>2. พัสดุ บรรจุภัณฑ์ และขนส่ง</ParcelFormSectionTitle>
      <Row gutter={16} align="top">
        <Col xs={24} lg={12}>
          <Form.Item
            name="parcel_name"
            label="พัสดุที่ต้องการส่ง"
            rules={[{ required: true, message: "กรุณาระบุพัสดุที่ต้องการส่ง" }]}
          >
            <Input
              className="parcel-field-input"
              size="small"
              style={parcelInputStyle}
              placeholder="ระบุพัสดุที่ต้องการส่ง"
              disabled={!sectionEnabled}
              onChange={() => onPackagingChange?.()}
            />
          </Form.Item>
          <ParcelHintBox>
            <Typography.Text type="secondary">{parcelHintText}</Typography.Text>
          </ParcelHintBox>

          <Typography.Text>ประเภทบรรจุภัณฑ์</Typography.Text>
          <Form.Item
            name="parent_packaging_type_id"
            rules={[{ required: true, message: "กรุณาเลือกประเภทบรรจุภัณฑ์" }]}
            style={{ marginBottom: 8, marginTop: 4 }}
          >
            <Select
              className="parcel-field-select"
              placeholder="เลือกประเภทบรรจุภัณฑ์"
              loading={packagingTypeLoading}
              allowClear
              size="small"
              style={{ width: "100%" }}
              disabled={!sectionEnabled}
              options={parentList.map((p) => ({ label: p.name, value: p.id }))}
              onChange={handleParentChange}
            />
          </Form.Item>

          {showChildDropdown && (
            <>
              <Typography.Text>บรรจุภัณฑ์ย่อย</Typography.Text>
              <Form.Item
                name="packaging_type_id"
                rules={[{ required: true, message: "กรุณาเลือกบรรจุภัณฑ์ย่อย" }]}
                style={{ marginBottom: 8, marginTop: 4 }}
              >
                <Select
                  className="parcel-field-select"
                  key={effectiveParentId ?? "no-parent"}
                  placeholder="เลือกขนาดกล่อง/ซอง"
                  loading={childPackagingTypeLoading}
                  allowClear
                  size="small"
                  style={{ width: "100%" }}
                  disabled={!sectionEnabled}
                  value={(() => {
                    const v = packagingTypeId;
                    const exists = childPackagingTypeData?.data?.master_packaging_types?.some(
                      (c) => c.id === v
                    );
                    return exists ? v : undefined;
                  })()}
                  options={childPackagingTypeData?.data?.master_packaging_types?.map((c) => ({
                    label: `${c.name}${
                      c.width != null ? ` (${c.width}x${c.length}x${c.height} ซม.)` : ""
                    }`,
                    value: c.id,
                  }))}
                  onChange={() => onPackagingChange?.()}
                />
              </Form.Item>
            </>
          )}

          {hideAutoDims && selectedPackaging?.width != null && (
            <Typography.Text type="secondary" style={{ display: "block", marginBottom: 8 }}>
              ขนาดกล่อง: {selectedPackaging.width} × {selectedPackaging.length} ×{" "}
              {selectedPackaging.height} ซ.ม.
            </Typography.Text>
          )}

          {showDimInputs && (
            <Row gutter={8}>
              <Col span={8}>
                <Form.Item
                  name="box_width"
                  label="กว้าง (ซม.)"
                  rules={[{ required: true, message: "กรุณากรอก" }]}
                >
                  <ParcelDigitsInput
                    min={1}
                    max={500}
                    size="small"
                    style={{ width: "100%" }}
                    disabled={!sectionEnabled}
                    onChange={() => onPackagingChange?.()}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="box_length"
                  label="ยาว (ซม.)"
                  rules={[{ required: true, message: "กรุณากรอก" }]}
                >
                  <ParcelDigitsInput
                    min={1}
                    max={500}
                    size="small"
                    style={{ width: "100%" }}
                    disabled={!sectionEnabled}
                    onChange={() => onPackagingChange?.()}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="box_height"
                  label="สูง (ซม.)"
                  rules={[{ required: true, message: "กรุณากรอก" }]}
                >
                  <ParcelDigitsInput
                    min={1}
                    max={500}
                    size="small"
                    style={{ width: "100%" }}
                    disabled={!sectionEnabled}
                    onChange={() => onPackagingChange?.()}
                  />
                </Form.Item>
              </Col>
            </Row>
          )}

          {hideAutoDims && (
            <>
              <Form.Item name="box_width" hidden>
                <InputNumber />
              </Form.Item>
              <Form.Item name="box_height" hidden>
                <InputNumber />
              </Form.Item>
              <Form.Item name="box_length" hidden>
                <InputNumber />
              </Form.Item>
            </>
          )}

          {dimSumInvalid && (
            <Alert
              type="error"
              showIcon
              message="กว้างยาวสูงรวมกันต้องไม่เกิน 1,000 ซ.ม."
              style={{ marginBottom: 8 }}
            />
          )}

          <Row gutter={8} align="bottom" style={{ marginBottom: 12 }}>
            <Col span={12}>
              <Form.Item
                name="box_weight"
                label="น้ำหนัก (กรัม)"
                rules={[{ required: true, message: "กรุณาระบุน้ำหนัก" }]}
                style={{ marginBottom: 0 }}
              >
                <ParcelDigitsInput
                  min={1}
                  max={100000}
                  size="small"
                  style={{ width: "100%" }}
                  placeholder="น้ำหนักรวม"
                  disabled={!sectionEnabled}
                  onChange={() => onPackagingChange?.()}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Button
                type="primary"
                block
                loading={checkingPrice}
                disabled={!sectionEnabled || !canSearch || dimSumInvalid}
                onClick={onSearchCarriers}
                style={{
                  height: parcelInputStyle.height,
                  ...parcelHighlightActionButtonStyle(canSearch),
                }}
              >
                ค้นหาบริษัทขนส่ง
              </Button>
            </Col>
          </Row>

          <Row gutter={8}>
            <Col span={12}>
              <Typography.Text>ค่าขนส่ง (บาท)</Typography.Text>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  marginTop: 4,
                  width: "100%",
                }}
              >
                <Input
                  className="parcel-field-input"
                  readOnly
                  size="small"
                  value={shippingPriceDisplay === "-" ? "" : shippingPriceDisplay}
                  placeholder="-"
                  style={{
                    flex: "0 1 auto",
                    textAlign: "right",
                    maxWidth: 140,
                    height: parcelInputStyle.height,
                  }}
                />
                {selectedItem?.logo ? (
                  <TransportLogo
                    src={normalizeSpeedLogo(selectedItem.logo)}
                    alt={selectedItem.name || "carrier"}
                    height={26}
                    width={64}
                  />
                ) : null}
                {selectedItem?.name ? (
                  <Typography.Text strong style={{ flex: 1, minWidth: 0 }}>
                    {selectedItem.name}
                  </Typography.Text>
                ) : null}
              </div>
            </Col>
            <Col span={12}>
              {(() => {
                // เครดิตที่ใช้ = ค่าขนส่งดิบ × multiplier (ให้ตรงกับโควต้าที่โรงงานเห็นจริง) — การ
                // เช็คพอ/ไม่พอ ยังเทียบกับ available_credit ดิบ ไม่เกี่ยว multiplier (ดู
                // handleOpenConfirm ใน ParcelCreateForm.tsx ที่เช็คก่อนสร้างจริง)
                const rawPrice = selectedItem
                  ? Math.ceil(Number(selectedItem.total ?? selectedItem.price ?? 0))
                  : 0;
                const hasPrice = !!selectedItem;
                const enough = availableCredit == null || availableCredit >= rawPrice;
                const creditDisplay = hasPrice
                  ? formatWithCommas(Math.ceil(rawPrice * (multiplier ?? 2)))
                  : "-";
                return (
                  <>
                    <Typography.Text>เครดิตที่ใช้</Typography.Text>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4, width: "100%", minHeight: parcelInputStyle.height }}>
                      <Typography.Text
                        strong
                        style={{ color: hasPrice ? (enough ? "#1890ff" : "#ff4d4f") : undefined }}
                      >
                        {hasPrice ? creditDisplay : "-"}
                      </Typography.Text>
                      <Typography.Text type="secondary">เครดิต</Typography.Text>
                      {hasPrice && !enough && (
                        <Typography.Text style={{ color: "#ff4d4f", fontSize: 12 }}>
                          (ไม่เพียงพอ)
                        </Typography.Text>
                      )}
                    </div>
                  </>
                );
              })()}
            </Col>
          </Row>

          <Form.Item name="cod_amount" hidden initialValue={0}>
            <InputNumber />
          </Form.Item>
        </Col>

        <Col xs={24} lg={12}>
          <Typography.Text strong style={{ display: "block", marginBottom: 8 }}>
            รายการบริษัทขนส่ง
          </Typography.Text>
          <ParcelCarrierPanel
            carriers={carrierList}
            selectedCarrier={selectedCarrier}
            onSelectCarrier={onSelectCarrier}
            speedHasSearched={speedHasSearched}
            loading={checkingPrice}
            showPanelTitle={false}
          />
        </Col>
      </Row>
    </ParcelFormZone>
  );
};

export default ParcelProductShippingStep;
